import React from 'react';
import { snakeCase, last, first, isEmpty, upperFirst, get } from 'lodash';

import * as Icons from './Icons';
import { isEmail } from '~/utils/String';
import { useRelativeDate } from '~/react-hooks/use-date';
import { attributeTypes, attributeData } from './AttributeTypes';
import ReplaceableComponents from '~/components/higher-order/ReplaceableComponents';
import { createFontAwesomeIcon } from './util';

export type AttributeValue = string | number | boolean | null | undefined;

export function getTypeFromValue(value: AttributeValue): string {
  switch (typeof value) {
    case 'number':
      return attributeTypes.NUMBER;
    case 'boolean':
      return attributeTypes.BOOLEAN;
    case 'string':
      if (isEmail(value)) {
        return attributeTypes.EMAIL;
      }
      break;
  }

  return attributeTypes.STRING;
}

function getTypeFromNameSegments(words: string[]): string {
  // email
  if (first(words) === 'email') {
    return attributeTypes.EMAIL;
  }

  // date
  if (last(words) === 'at') {
    return attributeTypes.DATE;
  }

  // boolean
  if (first(words) === 'is') {
    return attributeTypes.BOOLEAN;
  }

  // number
  if (words.some((word) => word === 'total')) {
    return attributeTypes.NUMBER;
  }

  return attributeTypes.STRING;
}

export const getTypeFromName = (name: string) =>
  getTypeFromNameSegments(snakeCase(name).split('_'));

export function getAttributeType(name: string, value: AttributeValue) {
  const typeFromName = getTypeFromName(name);

  return typeFromName !== attributeTypes.STRING
    ? typeFromName
    : getTypeFromValue(value);
}

export const getIconComponentForType = (
  type: string
): ReturnType<typeof createFontAwesomeIcon> => {
  switch (type) {
    case attributeTypes.STRING:
      return Icons.Default;
    case attributeTypes.NUMBER:
      return Icons.Number;
    case attributeTypes.DATE:
      return Icons.Date;
    case attributeTypes.BOOLEAN:
      return Icons.Boolean;
    case attributeTypes.URL:
      return Icons.Url;
    case attributeTypes.NAME:
      return Icons.Name;
    case attributeTypes.EMAIL:
      return Icons.Email;
    case attributeTypes.PHONE_NUMBER:
      return Icons.Phone;
    case attributeTypes.COMPANY_NAME:
      return Icons.CompanyName;
    case attributeTypes.COMPANY_TYPE:
      return Icons.CompanyType;
    case attributeTypes.COMPANY_POSITION:
      return Icons.JobTitle;
    case attributeTypes.COMPANY_SIZE:
      return Icons.CompanySize;
    case attributeTypes.IDENTIFIER:
      return Icons.Identifier;
    case attributeTypes.TIMEZONE:
      return Icons.Timezone;
    case attributeTypes.LOCATION:
      return Icons.Location;
    case attributeTypes.CURRENCY:
      return Icons.Currency;
    case attributeTypes.GENDER:
      return Icons.Gender;
    case attributeTypes.BIRTHDAY:
      return Icons.Birthday;
    case attributeTypes.METRIC:
      return Icons.Metric;
    case attributeTypes.LANGUAGE:
      return Icons.Language;
    case attributeTypes.PLAN:
      return Icons.Plan;
    case attributeTypes.BIO:
      return Icons.Bio;
    case attributeTypes.SOCIAL:
      return Icons.Social;
    case attributeTypes.TWITTER:
      return Icons.Twitter;
    case attributeTypes.FACEBOOK:
      return Icons.Facebook;
    case attributeTypes.LINKEDIN:
      return Icons.LinkedIn;
    default: {
      const subType = get(attributeData, [type, 'subtype']) as
        | string
        | undefined;
      if (subType != null) {
        return getIconComponentForType(subType);
      }

      return Icons.Default;
    }
  }
};

function formatUrl(url: string) {
  if (url == null) return null;

  return (
    <a href={url} target='_blank' rel='noopener noreferrer'>
      {url}
    </a>
  );
}

const formatBoolean = (value: boolean) => (value ? 'Yes' : 'No');

function stringToBoolean(value: string) {
  switch (value.trim().toLowerCase()) {
    case 'yes':
    case 'true':
    case '1':
      return true;
    case 'no':
    case 'false':
    case '0':
      return false;
    default:
      return !isEmpty(value);
  }
}

export const formatAsBoolean = (value: AttributeValue) => {
  switch (typeof value) {
    case 'boolean':
      return formatBoolean(value);
    case 'number':
      return formatBoolean(value > 0);
    case 'string':
      return formatBoolean(stringToBoolean(value));
    default:
      return value || null;
  }
};

function formatDate(value: number | string) {
  const relative = useRelativeDate(value);

  if (relative) {
    return upperFirst(relative);
  }
}

function formatEmail(value: string) {
  return <a href={`mailto:${value}`}>{value}</a>;
}

export const components = {
  ValueContainer: 'span',
};

export const formatters = {
  default: (value: AttributeValue) => value,
  nullValue: () => 'Unknown',
  [attributeTypes.BOOLEAN]: formatAsBoolean,
  [attributeTypes.DATE]: formatDate,
  [attributeTypes.EMAIL]: formatEmail,
  [attributeTypes.URL]: formatUrl,
};

export interface AttributeFormatterProps {
  className?: string;
  type: string;
  value: AttributeValue;
  components: {
    ValueContainer: React.ComponentClass<{
      className?: string;
      children: React.ReactNode;
    }>;
  };
  formatters: Record<string, (value: AttributeValue) => React.ReactText | null>;
}

function AttributeFormatter({
  value,
  type = attributeTypes.STRING,
  formatters,
  components,
  className,
}: AttributeFormatterProps) {
  const { ValueContainer } = components;

  function getFormatter() {
    if (value == null) {
      return formatters.nullValue;
    }

    return get(formatters, type, formatters.default);
  }

  function getFormattedValue(): React.ReactNode {
    const formatValue = getFormatter();

    return formatValue(value);
  }

  return (
    <ValueContainer className={className}>{getFormattedValue()}</ValueContainer>
  );
}

export default ReplaceableComponents(
  formatters,
  'formatters'
)(ReplaceableComponents(components)(AttributeFormatter));
